import React, { useState, useMemo } from 'react';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '20px',
  },
  tableWrapper: {
    overflowX: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    background: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  thRight: {
    textAlign: 'right',
  },
  td: {
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  tdRight: {
    textAlign: 'right',
  },
  sortArrow: {
    marginLeft: '4px',
    fontSize: '10px',
    color: '#9ca3af',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
};

const SPARKLINE_WIDTH = 80;
const SPARKLINE_HEIGHT = 20;

function formatPrice(value) {
  if (value == null) return '--';
  if (typeof value === 'number') return `$${value.toFixed(2)}`;
  return '--';
}

function formatRange(min, max) {
  if (min == null || max == null) return '--';
  return `$${Math.round(min)}-$${Math.round(max)}`;
}

function formatChangePercent(value) {
  if (value == null || value === 0) return '0.00%';
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function getChangeColor(value) {
  if (value == null || value === 0) return '#6b7280';
  return value > 0 ? '#dc2626' : '#16a34a';
}

function getSortArrow(sortKey, currentSort) {
  if (currentSort.key !== sortKey) return '';
  return currentSort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
}

function buildSparklinePath(points) {
  if (!points || points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;

  const padding = 2;
  const drawWidth = SPARKLINE_WIDTH - padding * 2;
  const drawHeight = SPARKLINE_HEIGHT - padding * 2;

  const coords = points.map((val, i) => {
    const x = padding + (i / (points.length - 1)) * drawWidth;
    const y = padding + drawHeight - ((val - min) / range) * drawHeight;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return coords.join(' ');
}

const COLUMNS = [
  { key: 'state', label: 'State', align: 'left' },
  { key: 'addressCount', label: 'Addresses', align: 'right' },
  { key: 'sparkline', label: 'Trend', align: 'left', sortable: false },
  { key: 'avgPrice', label: 'Avg Price', align: 'right' },
  { key: 'changePercent', label: 'Change%', align: 'right' },
  { key: 'range', label: 'Range', align: 'right', sortable: false },
  { key: 'volatility', label: 'Volatility', align: 'right' },
];

export default function SparkTable({ data }) {
  const [sortConfig, setSortConfig] = useState({ key: 'state', dir: 'asc' });
  const [hoveredRow, setHoveredRow] = useState(null);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aVal, bVal;

      if (sortConfig.key === 'range') {
        aVal = a.minPrice;
        bVal = b.minPrice;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortConfig.dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.dir === 'asc' ? 1 : -1;

      // Secondary sort by state
      if (sortConfig.key !== 'state') {
        const sa = (a.state || '').toLowerCase();
        const sb = (b.state || '').toLowerCase();
        if (sa < sb) return -1;
        if (sa > sb) return 1;
      }
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  const handleSort = (key) => {
    const col = COLUMNS.find((c) => c.key === key);
    if (col && col.sortable === false) return;
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State Summary</div>
        <div style={styles.subtitle}>Key metrics with trend sparklines per state</div>
        <div style={styles.emptyState}>No state summary data available</div>
      </div>
    );
  }

  function renderSparkline(row) {
    const points = row.sparkline;
    const pathStr = buildSparklinePath(points);
    if (!pathStr) return '--';

    const color = (row.changePercent != null && row.changePercent >= 0) ? '#3b82f6' : '#dc2626';

    return (
      <svg
        width={SPARKLINE_WIDTH}
        height={SPARKLINE_HEIGHT}
        style={{ display: 'block' }}
        viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
      >
        <polyline
          points={pathStr}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  function renderCellValue(col, row) {
    switch (col.key) {
      case 'state':
        return row.state || '--';
      case 'addressCount':
        return row.addressCount != null ? row.addressCount : '--';
      case 'sparkline':
        return renderSparkline(row);
      case 'avgPrice':
        return formatPrice(row.avgPrice);
      case 'changePercent':
        return (
          <span style={{ color: getChangeColor(row.changePercent), fontWeight: 600 }}>
            {formatChangePercent(row.changePercent)}
          </span>
        );
      case 'range':
        return formatRange(row.minPrice, row.maxPrice);
      case 'volatility':
        return row.volatility != null ? `${row.volatility.toFixed(1)}%` : '--';
      default:
        return '--';
    }
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>State Summary</div>
      <div style={styles.subtitle}>Key metrics with trend sparklines per state</div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...styles.th,
                    ...(col.align === 'right' ? styles.thRight : {}),
                    ...(col.sortable === false ? { cursor: 'default' } : {}),
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {col.sortable !== false && (
                    <span style={styles.sortArrow}>{getSortArrow(col.key, sortConfig)}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, idx) => {
              const isEven = idx % 2 === 0;
              const isHovered = hoveredRow === idx;
              const baseBg = isEven ? '#fff' : '#fafafa';
              const bg = isHovered ? '#f9fafb' : baseBg;

              return (
                <tr
                  key={row.state || idx}
                  style={{
                    backgroundColor: bg,
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={() => setHoveredRow(idx)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      style={{
                        ...styles.td,
                        ...(col.align === 'right' ? styles.tdRight : {}),
                      }}
                    >
                      {renderCellValue(col, row)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {sortedRows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af', padding: '24px' }}>
                  No state data matches the current sort
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
